import { LoaderCircleIcon, XIcon } from "lucide-react";
import { memo, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { createOwnedObjectUrl, releaseOwnedObjectUrl } from "~/workSurfaceResources";
import {
  filePreviewContentType,
  isImageFilePreview,
  isTextFilePreview,
  readBoundedFilePreview,
  type ExpandedFilePreview,
} from "./ExpandedFilePreview";

type PreviewState =
  | { readonly status: "loading" }
  | { readonly status: "text"; readonly contents: string }
  | { readonly status: "image"; readonly objectUrl: string }
  | { readonly status: "document"; readonly objectUrl: string }
  | { readonly status: "error"; readonly message: string };

export const ExpandedFileDialog = memo(function ExpandedFileDialog(props: {
  readonly preview: ExpandedFilePreview;
  readonly onClose: () => void;
}) {
  const [state, setState] = useState<PreviewState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void fetch(props.preview.src, { signal: controller.signal })
      .then(async (response) => {
        if (response.status === 404) {
          // The file moved or was deleted between opening this and clicking it.
          throw new Error("This file is no longer at the path it was attached from.");
        }
        if (!response.ok) throw new Error(`Preview request failed (${response.status}).`);
        const bytes = await readBoundedFilePreview(response);
        if (controller.signal.aborted) return;
        if (isTextFilePreview(props.preview)) {
          setState({ status: "text", contents: new TextDecoder().decode(bytes) });
          return;
        }
        const isImage = isImageFilePreview(props.preview);
        const contentType = filePreviewContentType(
          props.preview,
          response.headers.get("content-type"),
        );
        const previewBuffer = new Uint8Array(bytes.byteLength);
        previewBuffer.set(bytes);
        objectUrl = createOwnedObjectUrl(new Blob([previewBuffer.buffer], { type: contentType }), {
          ownerIdentity: "expanded-file-dialog",
          resourceIdentity: isImage ? "image-preview" : "document-preview",
          lifetime: "open-owner",
        });
        setState({ status: isImage ? "image" : "document", objectUrl });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "error",
          message: error instanceof Error ? error.message : "Could not preview this attachment.",
        });
      });
    return () => {
      controller.abort();
      if (objectUrl) releaseOwnedObjectUrl(objectUrl, "owner-disposal");
    };
  }, [props.preview]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      props.onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [props.onClose]);

  return (
    <div
      aria-label={`Preview attached file ${props.preview.name}`}
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-6 [-webkit-app-region:no-drag]"
      role="dialog"
    >
      <button
        aria-label="Close file preview"
        className="absolute inset-0 z-0"
        onClick={props.onClose}
        type="button"
      />
      <div className="relative z-10 flex h-[86vh] w-[min(92vw,70rem)] flex-col overflow-hidden rounded-xl border border-border/70 bg-background shadow-2xl">
        <header className="flex h-10 shrink-0 items-center justify-between border-b border-border/70 px-3">
          <p className="truncate text-sm font-medium">{props.preview.name}</p>
          <Button
            aria-label="Close file preview"
            onClick={props.onClose}
            size="icon-xs"
            variant="ghost"
          >
            <XIcon />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto">
          {state.status === "loading" ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
              <LoaderCircleIcon className="size-4 animate-spin" /> Loading preview…
            </div>
          ) : state.status === "error" ? (
            <div className="flex h-full items-center justify-center p-8 text-sm text-destructive">
              {state.message}
            </div>
          ) : state.status === "text" ? (
            <pre className="min-h-full whitespace-pre-wrap break-words p-5 font-mono text-xs leading-relaxed text-foreground">
              {state.contents}
            </pre>
          ) : state.status === "image" ? (
            <div className="flex h-full items-center justify-center p-4">
              <img
                alt={props.preview.name}
                className="max-h-full max-w-full object-contain"
                src={state.objectUrl}
              />
            </div>
          ) : (
            <iframe
              className="h-full w-full border-0 bg-white"
              sandbox=""
              src={state.objectUrl}
              title={`Attachment preview: ${props.preview.name}`}
            />
          )}
        </div>
      </div>
    </div>
  );
});
